import { CSSProperties, FC } from "react";

type CircularProgressProps = {
  progress: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
  loading?: boolean;
  withoutCheckMark?: boolean;
  completeBackgroundColor?: string;
  completeCheckMarkColor?: string;
  className?: string;
  style?: CSSProperties;
};

export const CircularProgress: FC<CircularProgressProps> = ({
  className,
  progress: p,
  max = 100,
  size = 24,
  strokeWidth = 3,
  color = "#18181B",
  trackColor = "#e6e6e6",
  withoutCheckMark = false,
  loading,
  completeBackgroundColor,
  completeCheckMarkColor,
  style
}) => {
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  const rawProgress = loading ? 0 : p;
  const upper = max > 0 ? max : 1;
  const ratio = rawProgress / upper;
  const safeRatio = Math.max(0, Math.min(ratio, 1));
  // const safeProgress = safeRatio * 100;
  const isComplete = rawProgress >= upper && !withoutCheckMark;

  const offset = circumference * (1 - safeRatio);
  const checkMarkPath = `M ${-radius * 0.3} 0 L ${-radius * 0.1} ${radius * 0.2} L ${radius * 0.3} ${-radius * 0.2}`;

  return (
    <div className={`relative inline-flex ${className}`} style={{ width: size, height: size, ...style }}>
      <svg width={size} height={size}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {loading && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#FF603E"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${circumference * 0.15} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          >
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              from={`0 ${center} ${center}`}
              to={`360 ${center} ${center}`}
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
        )}

        {!isComplete ? (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        ) : (
          <g transform={`translate(${center}, ${center})`}>
            <circle cx={0} cy={0} r={radius} fill={completeBackgroundColor || color} />
            <path
              d={checkMarkPath}
              fill="none"
              stroke={completeCheckMarkColor || "white"}
              strokeWidth={strokeWidth / 2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>
    </div>
  );
};
